/**
 * MedicalHistoryStepCard
 *
 * Dedicated dark card for the "Complete Medical History" plan step.
 * Uses the silk-brown textured design from the Medical History page:
 * progress ring, XP/level pills, and insight rows.
 */

import React from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import Svg, { Circle } from "react-native-svg";
import { LinearGradient } from "expo-linear-gradient";
import {
  Check,
  ArrowRight,
  Sparkles,
  Trophy,
  Pill,
  TestTubes,
  ShieldAlert,
} from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  MEDICAL_HISTORY_XP_PER_SECTION,
  getMedicalHistoryProgressSummary,
} from "@src/packages/medical-history/sectionRegistry";
import type { PlanAction } from "../plan.types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

type Props = {
  stepNumber: number;
  totalSteps: number;
  onAction?: (action: PlanAction) => void;
  onOpenPregnancyTrackingSetup?: () => void;
  showStepPill?: boolean;
  bottomMargin?: number;
};

const XP_PER_SECTION = MEDICAL_HISTORY_XP_PER_SECTION;

function getLevel(
  completedSections: number,
  total: number,
): {
  level: number;
  titleKey: string;
} {
  if (completedSections >= total) {
    return {
      level: 5,
      titleKey: "homepage.medical_history_step.level_health_champion",
    };
  }
  const pct = total > 0 ? completedSections / total : 0;
  if (pct >= 0.75) {
    return {
      level: 4,
      titleKey: "homepage.medical_history_step.level_wellness_pro",
    };
  }
  if (pct >= 0.5) {
    return {
      level: 3,
      titleKey: "homepage.medical_history_step.level_health_explorer",
    };
  }
  if (pct >= 0.25) {
    return {
      level: 2,
      titleKey: "homepage.medical_history_step.level_getting_started",
    };
  }
  return {
    level: 1,
    titleKey: "homepage.medical_history_step.level_newcomer",
  };
}

function useCompletion() {
  const { data: patient, isLoading } = usePatient();
  const summary = getMedicalHistoryProgressSummary(patient);

  if (isLoading || !patient) {
    return {
      isLoading,
      progressPercent: 0,
      completedCategories: 0,
      totalSections: summary.totalSections,
      xp: 0,
      level: getLevel(0, summary.totalSections),
    };
  }

  const completedCategories = summary.completedSections;
  const xp = completedCategories * XP_PER_SECTION;
  const level = getLevel(completedCategories, summary.totalSections);
  return {
    isLoading: false,
    progressPercent: summary.progressPercent,
    completedCategories,
    totalSections: summary.totalSections,
    xp,
    level,
  };
}

// ─── Progress ring (dark mode) ───

function ProgressRing({
  percent,
  size = 64,
}: {
  percent: number;
  size?: number;
}) {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={percent >= 100 ? "#4ADE80" : "rgba(255,255,255,0.7)"}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {percent >= 100 ? (
          <Trophy size={20} color="#4ADE80" fill="#4ADE80" />
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "700",
              color: "#FFFFFF",
            }}
          >
            {percent}%
          </Text>
        )}
      </View>
    </View>
  );
}

// ─── Main component ───

export default function MedicalHistoryStepCard({
  stepNumber,
  totalSteps,
  onAction,
  showStepPill = true,
  bottomMargin = 16,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistoryStepCard");
  const {
    isLoading,
    progressPercent,
    completedCategories,
    totalSections,
    xp,
    level,
  } = useCompletion();

  const isAllComplete = completedCategories >= totalSections;

  return (
    <View style={{ marginBottom: bottomMargin }}>
      {/* Step number pill */}
      {showStepPill ? (
        <View style={{ marginBottom: 8 }}>
          <View
            style={{
              alignSelf: "flex-start",
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              backgroundColor: isAllComplete
                ? "rgba(78, 115, 66, 0.1)"
                : "rgba(42, 37, 34, 0.06)",
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 8,
            }}
          >
            {isAllComplete && (
              <Check size={11} color={EMBER.colors.warm} strokeWidth={3} />
            )}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "600",
                color: isAllComplete
                  ? EMBER.colors.warm
                  : EMBER.colors.textLight,
                letterSpacing: 0.3,
              }}
            >
              {isAllComplete
                ? t("homepage.shared.step_n_complete", {
                    params: { stepNumber: String(stepNumber) },
                  })
                : t("homepage.shared.step_n_of_total", {
                    params: {
                      stepNumber: String(stepNumber),
                      totalSteps: String(totalSteps),
                    },
                  })}
            </Text>
          </View>
        </View>
      ) : null}

      {/* Dark card */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("homepage.shared.continue_medical_history")),
            "homepage.shared.continue_medical_history",
          );
          onAction?.({
            label: t("homepage.shared.continue_medical_history"),
            variant: "primary",
            screen: "MedicalHistory",
          });
        }}
        style={{
          borderRadius: 24,
          overflow: "hidden",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.12,
          shadowRadius: 24,
          ...(isAllComplete && { opacity: 0.75 }),
        }}
      >
        {/* Silk texture */}
        <Image
          source={silkBrown}
          style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
          resizeMode="cover"
        />

        {/* Glass overlay */}
        <View
          style={{
            padding: 17,
            backgroundColor: "rgba(255,255,255,0.12)",
            borderRadius: 24,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.18)",
            ...(Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(40px)",
                } as Record<string, string>)
              : {}),
          }}
        >
          {/* Title + ring */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 13,
            }}
          >
            <ProgressRing percent={isLoading ? 0 : progressPercent} />
            <View style={{ flex: 1 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    flexShrink: 1,
                    fontSize: 19,
                    lineHeight: 24,
                    fontWeight: "700",
                    color: "#FFFFFF",
                    letterSpacing: -0.3,
                  }}
                >
                  {t("homepage.medical_history_step.title")}
                </Text>
                {!isLoading && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                      backgroundColor: "rgba(255,255,255,0.08)",
                      paddingHorizontal: 9,
                      paddingVertical: 4,
                      borderRadius: 999,
                    }}
                  >
                    <Sparkles
                      size={11}
                      color="rgba(255,255,255,0.75)"
                      strokeWidth={1.5}
                    />
                    <Text
                      className="font-dm-sans"
                      numberOfLines={1}
                      style={{
                        fontSize: 10,
                        fontWeight: "600",
                        color: "rgba(255,255,255,0.75)",
                      }}
                    >
                      {t("homepage.medical_history_step.level_xp", {
                        params: {
                          title: t(level.titleKey),
                          xp: String(xp),
                        },
                      })}
                    </Text>
                  </View>
                )}
              </View>
              {isLoading || isAllComplete ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 4,
                    lineHeight: 17,
                  }}
                >
                  {isLoading
                    ? t("homepage.medical_history_step.loading")
                    : t("homepage.medical_history_step.all_sections_complete")}
                </Text>
              ) : (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    columnGap: 5,
                    rowGap: 5,
                    marginTop: 7,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11.5,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.56)",
                      lineHeight: 16,
                    }}
                  >
                    {t(
                      "homepage.medical_history_step.complete_history_unlocks",
                    )}
                  </Text>
                  {[
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_treatment_personalisation",
                      icon: Sparkles,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_medication_safety",
                      icon: Pill,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_risk_screening",
                      icon: ShieldAlert,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_clinic_summaries",
                      icon: TestTubes,
                    },
                  ].map(({ labelKey, icon: Icon }) => (
                    <View
                      key={labelKey}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Icon size={11} color="#FFFFFF" strokeWidth={1.8} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11.5,
                          lineHeight: 16,
                          fontWeight: "700",
                          color: "#FFFFFF",
                        }}
                      >
                        {t(labelKey)}
                      </Text>
                    </View>
                  ))}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11.5,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.56)",
                      lineHeight: 16,
                    }}
                  >
                    {" "}
                    {t("homepage.medical_history_step.and_more")}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* CTA button */}
          {!isLoading && !isAllComplete && (
            <View style={{ marginTop: 18 }}>
              <LinearGradient
                colors={[
                  "rgba(255, 255, 255, 0.12)",
                  "rgba(255, 255, 255, 0.06)",
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderWidth: 1,
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 14,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "500",
                    color: "#ffffff",
                    letterSpacing: 0.3,
                  }}
                >
                  {t("homepage.medical_history_step.continue")}
                </Text>
                <ArrowRight size={15} color="#ffffff" strokeWidth={2} />
              </LinearGradient>
            </View>
          )}
        </View>
      </Pressable>
    </View>
  );
}
